import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { Box, Autosuggest, Stack, Heading, Text, TextLink } from "braid-design-system";
import { styles } from "./styles/ClampLines/ClampLines.css.mjs";
import { pseudoDot, textTone } from "./styles/DotSeparator/DotSeparator.css.mjs";
import { useApolloClient } from "@apollo/client";
import { useState, useEffect, useMemo } from "react";
import gql from "graphql-tag";
import { relatedSearchWrapper } from "./styles/RelatedSearch/RelatedSearch.css.mjs";
const ClampLines = ({
  numLines = "1",
  originalText = "",
  children
}) => {
  const { base, wordBreak, lineClamp } = styles;
  const clampStyle = lineClamp[numLines];
  const wordBreakStyle = numLines === "1" ? wordBreak.singleLine : wordBreak.multiLine;
  return /* @__PURE__ */ jsx(
    Box,
    {
      className: [base, clampStyle, wordBreakStyle],
      ...originalText ? { title: originalText } : {},
      children
    }
  );
};
const dotText = "•";
const DotSeparator = ({ content }) => /* @__PURE__ */ jsx(Fragment, { children: content.map((item, index) => {
  const { text, tone } = typeof item === "string" ? { text: item, tone: void 0 } : item;
  return /* @__PURE__ */ jsx(
    Box,
    {
      display: "inline",
      className: [pseudoDot, tone && textTone[tone]],
      children: text
    },
    `dotSeparator_${index}`
  );
}) });
const GET_KEYWORD_SUGGESTIONS = gql`
  query getKeywordSuggestions($keyword: String!, $country: CountryCodeIso2!) {
    searchKeywordsSuggest(query: $keyword, count: 5, countryCode2: $country) {
      suggestions {
        ... on KeywordSuggestion {
          text
          highlights {
            start
            end
          }
        }
      }
    }
  }
`;
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};
const mapTextToValue = (arr) => (arr || []).map((el) => ({ ...el, value: el.text }));
const mapSuggestionsResponse = (data) => {
  const { searchKeywordsSuggest } = data || {
    searchKeywordsSuggest: { suggestions: [] }
  };
  return mapTextToValue(searchKeywordsSuggest.suggestions);
};
const noop$1 = () => {
};
const KeywordAutoSuggest = ({
  initialSuggestions = [],
  keyword = { text: "" },
  onChange,
  onClear = noop$1,
  onBlur = noop$1,
  country,
  delay = 200,
  ...rest
}) => {
  const client = useApolloClient();
  const debouncedKeywordText = useDebounce(keyword.text, delay);
  const debouncedKeywordLength = Object.keys(debouncedKeywordText).length;
  const [storedSuggestions, setStoredSuggestions] = useState([]);
  useEffect(() => {
    if (debouncedKeywordLength > 1) {
      client.query({
        query: GET_KEYWORD_SUGGESTIONS,
        variables: { country, keyword: debouncedKeywordText }
      }).then(({ data }) => {
        const suggestions = mapSuggestionsResponse(data);
        setStoredSuggestions(suggestions);
      }).catch((error) => {
        if (error) {
          console.error("KeywordSuggestion", error);
        }
      });
    }
  }, [client, country, debouncedKeywordLength, debouncedKeywordText]);
  const onKeywordChange = ({
    text,
    value
  }) => {
    const newKeyword = {
      text,
      value,
      previousText: debouncedKeywordText,
      isSuggestion: value !== void 0
    };
    onChange(newKeyword);
  };
  const onClearLocal = () => {
    setStoredSuggestions([]);
    onClear();
  };
  return /* @__PURE__ */ jsx(
    Autosuggest,
    {
      suggestions: debouncedKeywordLength > 1 ? storedSuggestions : initialSuggestions || [],
      value: keyword,
      onChange: onKeywordChange,
      onClear: onClearLocal,
      onBlur,
      ...rest
    }
  );
};
const paddingX = { mobile: "small", desktop: "xlarge" };
const PageInset = ({ children }) => /* @__PURE__ */ jsx(Box, { paddingX, children });
PageInset.paddingX = paddingX;
const TitledContent = ({
  title,
  children,
  headingLevel,
  headingDataAutomation,
  space = "none"
}) => {
  const headingData = headingDataAutomation ? { automation: headingDataAutomation } : void 0;
  return /* @__PURE__ */ jsxs(Stack, { space, children: [
    headingLevel && /* @__PURE__ */ jsx(Heading, { level: headingLevel, data: headingData, children: title }) || /* @__PURE__ */ jsx(Text, { weight: "strong", data: headingData, children: title }),
    children ? /* @__PURE__ */ jsx(Fragment, { children }) : null
  ] });
};
const noop = () => {
};
const RelatedSearch = ({
  title,
  links,
  titleSpace = "medium",
  onLinkClick = noop
}) => {
  const renderedLinks = useMemo(
    () => links.map((link, index) => {
      const { text, props } = link;
      const key = `related_search_${index}`;
      return /* @__PURE__ */ jsxs(
        Box,
        {
          marginBottom: "small",
          width: "full",
          "data-automation": key,
          children: [
            " ",
            /* @__PURE__ */ jsx(Text, { baseline: false, children: /* @__PURE__ */ jsx(TextLink, { ...props, onClick: () => onLinkClick(link, index), children: text }) })
          ]
        },
        key
      );
    }),
    [links, onLinkClick]
  );
  return /* @__PURE__ */ jsx(
    TitledContent,
    {
      headingLevel: "4",
      headingDataAutomation: "related_search_heading",
      space: titleSpace,
      title,
      children: /* @__PURE__ */ jsx(Box, { className: relatedSearchWrapper, children: renderedLinks })
    }
  );
};
const Space = ({ size }) => /* @__PURE__ */ jsx(Box, { paddingTop: size, paddingLeft: size });
export {
  ClampLines,
  DotSeparator,
  KeywordAutoSuggest,
  PageInset,
  RelatedSearch,
  Space,
  TitledContent,
  dotText
};
